
import { UserViewModel } from "../ViewModels";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainDetailHook,
    DomainHookProps,
    UIAction,
    useAppEnum,
    useDetailLifeCycle,
} from "@/Shared";

import { useUser } from "@/OneLiners";
import { useGeneratePassword } from "./useGeneratePassword";
import { useUserEnricher } from "./useUserEnricher";
import { UserModel } from "../UserModel";


/**
 * useUserDetail hook
 *
 * Domain-specific detail hook for User entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - UI feedback mechanisms (toast notifications, confirmation dialogs),
 * - and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the UserViewModel when data is available.
 * - Handle password reset and delete operations with access control.
 * - Manage navigation (form, detail view, layout) based on user actions and permissions.
 * - Expose entity VM, UI actions, access rights, and navigation methods to the UI layer.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainDetailHook<UserViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<UserViewModel>
 */
export function useUserDetail(props: DomainHookProps): DomainDetailHook<UserViewModel> {
    const { permissionHook, accessRight, uiFeedbackHooks } = props;

    const { domainType, domainLabel } = DOMAIN_CONFIGS.User;

    /**
     * User-related enums exposed to the UI layer
     */
    const { UserEnums } = useAppEnum();

    /**
    * User domain CRUD operations and state
    */
    const userHook = useUser();

    /**
     * User domain enrichment facade
     */
    const { getStrictEnrichedUser, enricherResponse } = useUserEnricher();

    /**
     * Password generation action handler
     */
    const { handleGeneratePassword, response: generatePasswordResponse } 
    = useGeneratePassword(uiFeedbackHooks, accessRight);

    /**
     * Combined response acting as a single readiness contract
     * for initializing the detail view
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            userHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: userHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: userHook,
        buildVM: (currentEntity: UserModel, isDisplaying: boolean) => {
            return new UserViewModel({
                user: getStrictEnrichedUser(currentEntity),
                viewEnums: UserEnums,
                accessRight,
                isDisplaying
            })
        },
    });

    const { entityVM, requestId } = lifeCycleHook;

    /**
     * Computes UI actions available for the current user entity
     *
     * Includes:
     * - "Mot de passe" for password reset
     */
    const computeUIActions = (): UIAction[] => {
        const actions: UIAction[] = [];
        if (!entityVM || !requestId || entityVM?.isDeleted) {
            return actions;
        }

        const canExecute = accessRight.canExecute(domainType);
        actions.push({
            key: `${domainType}-execute-${requestId}`,
            label: "Mot de passe",
            onClick: () => canExecute
                ? handleGeneratePassword(entityVM.model)
                : undefined,

            placement: "sub",
            type: 'execute',
            icon: 'FaLock',
            disabled: !canExecute,
            loading: ActionStatusHelper.isLoading(generatePasswordResponse.status),
            tooltip: canExecute
                ? `Cliquez pour réinitialiser mot de passe "${domainLabel}".`
                : `Vous n'avez pas les droits suffisants pour réinitialiser le mot de pass "${domainLabel}".`
        });

        return actions
    }

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook,
        computeUIActions
    }
}